import type { DragEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Icon } from '../../../design-system/icons';
import { ActionsMenu, type MenuAction } from '../../../design-system/primitives';
import type { Selection } from '../selection';
import type { DropTarget } from './FolderChrome';
import { FolderNameInput } from './FolderDialogs';
import type { FolderEntry } from './folder-source';

/**
 * Affichages d'une liste à dossiers (ADR-149 §1, §2, §4) : grille à vignettes ou tableau, sous-dossiers
 * en tête. Une case par carte et par ligne, dossiers compris ; en présentation drive, un clic sur la
 * carte la sélectionne et le double-clic (ou Entrée) l'ouvre ; ailleurs le clic ouvre. Maj+clic : une
 * plage ; Ctrl+clic : ajouter ou retirer. Une carte se glisse sur un dossier pour l'y ranger.
 */

export interface ItemPresenter<Item> {
  id(item: Item): string;
  title(item: Item): string;
  /** Pastille de format (« PDF », « JPG ») et son ton. */
  badge(item: Item): { text: string; tone: 'danger' | 'info' | 'ok' | 'warning' | 'neutral' };
  /** Vignette servie (URL signée), si elle existe. */
  thumbnail?(item: Item): string | undefined;
  /** Ligne sous la carte : « Déposé le 19/10/2026 · 24 Ko ». */
  meta(item: Item): string;
  /** Valeur affichée d'une colonne déclarée de la vue, déjà formatée. */
  cell(field: string, item: Item): ReactNode;
  /** Menu « ⋮ » d'un élément. */
  menu(item: Item): MenuAction[];
}

/**
 * Pastille de format (PDF, JSON, XLSX, JPG…) : une seule taille par emplacement, quelle que soit
 * l'extension (retour de l'utilisateur du 2026-10-20) — largeur et hauteur fixes, texte centré, quatre
 * lettres au plus, réduit pour tenir. Même composant en liste, en grille et dans le panneau.
 */
export function FormatBadge({ text, tone, size = 'sm' }: { text: string; tone: 'danger' | 'info' | 'ok' | 'warning' | 'neutral'; size?: 'sm' | 'lg' }) {
  const shown = text.slice(0, 4).toUpperCase();
  return <span className={`ay-format-badge ay-format-badge--${size} ay-format-badge--${tone}`} data-length={shown.length}>{shown}</span>;
}

export const folderKey = (id: string): string => `f:${id}`;
export const itemKey = (id: string): string => `i:${id}`;
export const DRAG_TYPE = 'application/x-ayumia-item';

export interface FolderViewProps<Item> {
  folders: readonly FolderEntry[];
  items: readonly Item[];
  presenter: ItemPresenter<Item>;
  /** Colonnes déclarées par la représentation Table de la vue (hors nom). */
  columns: ReadonlyArray<{ field: string; label: string; align?: 'start' | 'end' }>;
  selection: Selection | undefined;
  clickSelects: boolean;
  /** Élément ouvert dans le panneau (clé). */
  openKey: string | null;
  itemsLabel: string;
  folderCount: (folder: FolderEntry) => string;
  folderMenu: (folder: FolderEntry) => MenuAction[];
  onOpenFolder: (folderId: string) => void;
  onOpenItem: (item: Item) => void;
  renaming: string | null;
  onRename: (folder: FolderEntry, name: string) => Promise<boolean>;
  onCancelRename: () => void;
  creating: boolean;
  onCreate: (name: string) => Promise<boolean>;
  onCancelCreate: () => void;
  dragFrom: (key: string) => { draggable?: true; onDragStart?: (event: DragEvent<HTMLElement>) => void };
  dropTarget: (folderId: string | null) => DropTarget;
  /** Fin de la grille : carte de dépôt, si la page sait déposer. */
  trailing?: ReactNode;
}

/** Clic sur une carte ou une ligne : sélection (drive, ou avec une touche) sinon ouverture. */
function activate(selection: Selection | undefined, clickSelects: boolean, key: string, event: MouseEvent<HTMLElement>, open: () => void): void {
  const modified = event.shiftKey || event.ctrlKey || event.metaKey;
  if (selection !== undefined && (clickSelects || modified)) selection.pick(key, event, clickSelects ? 'replace' : 'toggle');
  else open();
}

function keyboard(selection: Selection | undefined, key: string, event: KeyboardEvent<HTMLElement>, open: () => void): void {
  if (event.target !== event.currentTarget) return;
  if (event.key === 'Enter') {
    event.preventDefault();
    open();
  } else if (event.key === ' ' && selection !== undefined) {
    event.preventDefault();
    selection.pick(key, event, 'toggle');
  }
}

function Check({ selection, keyOf, label }: { selection: Selection | undefined; keyOf: string; label: string }) {
  if (selection === undefined) return null;
  return <input type="checkbox" className="ay-folders__check" aria-label={label} checked={selection.has(keyOf)} onChange={() => undefined} onClick={(event) => { event.stopPropagation(); selection.pick(keyOf, event, 'toggle'); }} />;
}

function FolderTileName({ folder, props }: { folder: FolderEntry; props: FolderViewProps<unknown> }) {
  if (props.renaming === folder.id) return <FolderNameInput label={`Nouveau nom de « ${folder.name} »`} initial={folder.name} onSubmit={(name) => props.onRename(folder, name)} onCancel={props.onCancelRename} />;
  return (
    <span className="ay-folders__tile-text">
      <strong className="ay-folders__tile-name">{folder.name}</strong>
      <span className="ay-folders__tile-count">{props.folderCount(folder)}</span>
    </span>
  );
}

export function FolderGrid<Item>(props: FolderViewProps<Item>) {
  const { folders, items, presenter, selection, clickSelects } = props;
  const shared = props as unknown as FolderViewProps<unknown>;
  return (
    <div className="ay-folders__grid-wrap">
      {(folders.length > 0 || props.creating) && (
        <section aria-label="Dossiers">
          <h2 className="ay-folders__heading">Dossiers</h2>
          <div className="ay-folders__folder-grid">
            {props.creating && (
              <div className="ay-folders__folder ay-folders__folder--editing">
                <Icon name="folder" size={18} />
                <FolderNameInput label="Nom du nouveau dossier" initial="" onSubmit={props.onCreate} onCancel={props.onCancelCreate} />
              </div>
            )}
            {folders.map((folder) => {
              const key = folderKey(folder.id);
              const checked = selection?.has(key) ?? false;
              const drop = props.dropTarget(folder.id);
              return (
                <div
                  key={folder.id}
                  className={`ay-folders__folder${checked ? ' ay-folders__folder--checked' : ''}${drop['data-drop-over'] === true ? ' ay-folders__folder--over' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label={folder.name}
                  aria-pressed={selection === undefined ? undefined : checked}
                  onClick={(event) => props.renaming !== folder.id && activate(selection, clickSelects, key, event, () => props.onOpenFolder(folder.id))}
                  onDoubleClick={() => props.onOpenFolder(folder.id)}
                  onKeyDown={(event) => keyboard(selection, key, event, () => props.onOpenFolder(folder.id))}
                  {...(folder.locked ? {} : props.dragFrom(key))}
                  {...drop}
                >
                  <Check selection={selection} keyOf={key} label={`Sélectionner le dossier ${folder.name}`} />
                  <Icon name="folder" size={18} />
                  <FolderTileName folder={folder} props={shared} />
                  {folder.locked && <span className="ay-folders__system" title="Dossier système : ni renommé, ni déplacé, ni archivé">Système</span>}
                  <span onClick={(event) => event.stopPropagation()} onDoubleClick={(event) => event.stopPropagation()}>
                    <ActionsMenu label={`Actions sur le dossier ${folder.name}`} actions={props.folderMenu(folder)} />
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}
      <section aria-label={props.itemsLabel}>
        {folders.length > 0 && <h2 className="ay-folders__heading">{props.itemsLabel}</h2>}
        <div className="ay-folders__item-grid">
          {items.map((item) => {
            const id = presenter.id(item);
            const key = itemKey(id);
            const checked = selection?.has(key) ?? false;
            const title = presenter.title(item);
            const badge = presenter.badge(item);
            const thumb = presenter.thumbnail?.(item);
            return (
              <article
                key={id}
                className={`ay-folders__card${checked ? ' ay-folders__card--checked' : ''}${props.openKey === key ? ' ay-folders__card--open' : ''}`}
                role="button"
                tabIndex={0}
                aria-label={title}
                aria-pressed={selection === undefined ? undefined : checked}
                onClick={(event) => activate(selection, clickSelects, key, event, () => props.onOpenItem(item))}
                onDoubleClick={() => props.onOpenItem(item)}
                onKeyDown={(event) => keyboard(selection, key, event, () => props.onOpenItem(item))}
                {...props.dragFrom(key)}
              >
                <div className="ay-folders__card-head">
                  <Check selection={selection} keyOf={key} label={`Sélectionner ${title}`} />
                  <FormatBadge text={badge.text} tone={badge.tone} />
                  <strong className="ay-folders__card-title" title={title}>{title}</strong>
                  <span onClick={(event) => event.stopPropagation()} onDoubleClick={(event) => event.stopPropagation()}>
                    <ActionsMenu label={`Actions sur ${title}`} actions={presenter.menu(item)} />
                  </span>
                </div>
                <div className="ay-folders__thumb">{thumb !== undefined ? <img src={thumb} alt="" loading="lazy" /> : <FormatBadge text={badge.text} tone={badge.tone} size="lg" />}</div>
                <span className="ay-folders__card-meta">{presenter.meta(item)}</span>
              </article>
            );
          })}
          {props.trailing}
        </div>
      </section>
    </div>
  );
}

export function FolderTable<Item>(props: FolderViewProps<Item>) {
  const { folders, items, presenter, selection, clickSelects, columns } = props;
  const shared = props as unknown as FolderViewProps<unknown>;
  const keys = [...folders.map((folder) => folderKey(folder.id)), ...items.map((item) => itemKey(presenter.id(item)))];
  const page = selection === undefined ? 'none' : selection.coverage(keys);
  return (
    <div className="ay-folders__table">
      <table>
        <thead>
          <tr>
            {selection !== undefined && (
              <th className="ay-folders__col-check" scope="col">
                <input
                  type="checkbox"
                  aria-label="Tout sélectionner sur cette page"
                  checked={page === 'all'}
                  ref={(input) => {
                    if (input !== null) input.indeterminate = page === 'some';
                  }}
                  onChange={() => (page === 'all' ? selection.clear() : selection.replace(keys))}
                />
              </th>
            )}
            <th scope="col">Nom</th>
            {columns.map((column) => (
              <th key={column.field} scope="col" className={column.align === 'end' ? 'ay-num' : undefined}>{column.label}</th>
            ))}
            <th className="ay-folders__col-menu" scope="col"><span className="ay-visually-hidden">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {props.creating && (
            <tr className="ay-folders__row--editing">
              {selection !== undefined && <td />}
              <td colSpan={columns.length + 2}>
                <span className="ay-folders__row-title"><Icon name="folder" size={18} /><FolderNameInput label="Nom du nouveau dossier" initial="" onSubmit={props.onCreate} onCancel={props.onCancelCreate} /></span>
              </td>
            </tr>
          )}
          {folders.length > 0 && (
            <tr className="ay-folders__group">
              <th scope="colgroup" colSpan={columns.length + (selection === undefined ? 2 : 3)}>Dossiers</th>
            </tr>
          )}
          {folders.map((folder) => {
            const key = folderKey(folder.id);
            const checked = selection?.has(key) ?? false;
            const drop = props.dropTarget(folder.id);
            return (
              <tr key={folder.id} className={`${checked ? 'is-picked' : ''}${drop['data-drop-over'] === true ? ' is-over' : ''}`} tabIndex={0} onClick={(event) => props.renaming !== folder.id && activate(selection, clickSelects, key, event, () => props.onOpenFolder(folder.id))} onDoubleClick={() => props.onOpenFolder(folder.id)} onKeyDown={(event) => keyboard(selection, key, event, () => props.onOpenFolder(folder.id))} {...(folder.locked ? {} : props.dragFrom(key))} {...drop}>
                {selection !== undefined && <td className="ay-folders__col-check"><Check selection={selection} keyOf={key} label={`Sélectionner le dossier ${folder.name}`} /></td>}
                <td>
                  <span className="ay-folders__row-title">
                    <Icon name="folder" size={18} />
                    {props.renaming === folder.id ? <FolderTileName folder={folder} props={shared} /> : <button type="button" className="ay-folders__row-open" aria-label={folder.name} onClick={(event) => { event.stopPropagation(); props.onOpenFolder(folder.id); }}>{folder.name}</button>}
                    {folder.locked && <span className="ay-folders__system" title="Dossier système : ni renommé, ni déplacé, ni archivé">Système</span>}
                  </span>
                </td>
                {columns.map((column, index) => (
                  <td key={column.field} className={`ay-folders__muted${column.align === 'end' ? ' ay-num' : ''}`}>{index === 0 ? `Dossier · ${props.folderCount(folder)}` : '—'}</td>
                ))}
                <td className="ay-folders__col-menu" onClick={(event) => event.stopPropagation()}><ActionsMenu label={`Actions sur le dossier ${folder.name}`} actions={props.folderMenu(folder)} /></td>
              </tr>
            );
          })}
          {folders.length > 0 && items.length > 0 && (
            <tr className="ay-folders__group">
              <th scope="colgroup" colSpan={columns.length + (selection === undefined ? 2 : 3)}>{props.itemsLabel}</th>
            </tr>
          )}
          {items.map((item) => {
            const id = presenter.id(item);
            const key = itemKey(id);
            const checked = selection?.has(key) ?? false;
            const title = presenter.title(item);
            const badge = presenter.badge(item);
            const thumb = presenter.thumbnail?.(item);
            return (
              <tr key={id} className={`${checked || props.openKey === key ? 'is-picked' : ''}`} tabIndex={0} onClick={(event) => activate(selection, clickSelects, key, event, () => props.onOpenItem(item))} onDoubleClick={() => props.onOpenItem(item)} onKeyDown={(event) => keyboard(selection, key, event, () => props.onOpenItem(item))} {...props.dragFrom(key)}>
                {selection !== undefined && <td className="ay-folders__col-check"><Check selection={selection} keyOf={key} label={`Sélectionner ${title}`} /></td>}
                <td>
                  <span className="ay-folders__row-title">
                    {thumb !== undefined ? <img src={thumb} alt="" /> : <FormatBadge text={badge.text} tone={badge.tone} />}
                    <button type="button" className="ay-folders__row-open" onClick={(event) => { event.stopPropagation(); props.onOpenItem(item); }}>{title}</button>
                  </span>
                </td>
                {columns.map((column) => (
                  <td key={column.field} className={column.align === 'end' ? 'ay-num' : 'ay-folders__muted'}>{presenter.cell(column.field, item)}</td>
                ))}
                <td className="ay-folders__col-menu" onClick={(event) => event.stopPropagation()}><ActionsMenu label={`Actions sur ${title}`} actions={presenter.menu(item)} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
